import { GetDataForThanksForRSVPQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import React, { useMemo, useState } from 'react';
import { Success } from '../Success/Success';
import { AccommodationsPushStep } from '../AccommodationsPushStep/AccommodationsPushStep';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useAccommodationStepPushCohort } from '@apps/thanksForRsvp/hooks/useAccommodationStepPushCohort';
import { NearbyHotelsData } from '@apps/thanksForRsvp/hooks/useNearbyHotelsData';
import { useRsvpFeatureValue } from '@shared/hooks/useRsvpFeatureFlags';

const SUCCESS_SCREEN_VIEW = ['accommodationsPush', 'success'] as const;
type SuccessScreenView = (typeof SUCCESS_SCREEN_VIEW)[number];

interface SuccessScreenSwitch
  extends Readonly<{
    // Following props were taken direcrtly from Success.tsx
    eventHandle: string;
    eventId: string;
    queryData: GetDataForThanksForRSVPQuery | undefined;
    fromGiftWrap: boolean;
    accommodationsCardVisibilityData: {
      isAccommodationsPageEnabled?: boolean;
    };
    forceSkipAccommodationsPushStep?: boolean;
    nearbyHotelsData: NearbyHotelsData;
    showRegistryCard: boolean;
  }> {}

export const SuccessScreenSwitch = (props: SuccessScreenSwitch) => {
  const { forceSkipAccommodationsPushStep, queryData } = props;
  const eventAccCheckResult = useEventAccommodationsCheck({
    eventHandle: props.eventHandle,
  });

  // https://withjoy.atlassian.net/browse/PRNT-2183
  const isUsePostRsvpSmsEventFeatureFlagEnabled = useMemo(() => {
    return queryData?.eventByName?.featureFlags?.includes('usePostRsvpSms');
  }, [queryData]);

  const accommodationStepPushCohort = useAccommodationStepPushCohort(eventAccCheckResult);

  const isAccommodationsStepForNoAccomsCohortEnabled =
    useRsvpFeatureValue('rsvpGuestFlowAccommodationsStep', {
      skip: forceSkipAccommodationsPushStep || accommodationStepPushCohort !== 'hasNoAccommodationsAndTravelPages' || isUsePostRsvpSmsEventFeatureFlagEnabled,
    }).value === 'treatment';
  const [stepIndex, setStepIndex] = useState<number>(accommodationStepPushCohort !== undefined && !forceSkipAccommodationsPushStep ? 0 : 1);

  const currentStep: SuccessScreenView = SUCCESS_SCREEN_VIEW[stepIndex] ?? 'success';

  const goForward = useEventCallback(() => {
    setStepIndex(prev => Math.max(prev + 1, SUCCESS_SCREEN_VIEW.length - 1));
  });

  switch (currentStep) {
    case 'accommodationsPush': {
      if (
        accommodationStepPushCohort === 'hasOwnAccommodations' ||
        (accommodationStepPushCohort === 'hasNoAccommodationsAndTravelPages' && isAccommodationsStepForNoAccomsCohortEnabled)
      ) {
        return (
          <AccommodationsPushStep
            eventId={props.eventId}
            eventHandle={props.eventHandle}
            queryData={props.queryData}
            goForward={goForward}
            {...eventAccCheckResult}
            cohort={accommodationStepPushCohort}
          />
        );
      }
    }

    default: {
      return <Success {...props} />;
    }
  }
};
